import React, { useState, useRef, useEffect } from 'react';
import {
    ActivityIndicator,
    Modal,
    Pressable,
    StyleSheet,
    Switch,
    Text,
    View,
} from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import DataOps from '../../../../data/DataOps';
import { v4 as uuidv4 } from 'uuid';

interface WaiverEditModalProps {
    visible: boolean;
    onClose: () => void;
    sessionId: string;
    currentWaiverRequired: boolean;
    currentWaiverTitle?: string;
    currentWaiverUrl?: string;
    onSave: (waiverRequired: boolean, waiverTitle: string | null, waiverUrl: string | null) => void;
    sessionTitle?: string;
}

const ACCEPTED_TYPES = '.pdf,.doc,.docx';
const MAX_MB = 10;

const WaiverEditModal: React.FC<WaiverEditModalProps> = ({
    visible,
    onClose,
    sessionId,
    currentWaiverRequired,
    currentWaiverTitle,
    currentWaiverUrl,
    onSave,
    sessionTitle = 'Session Waiver',
}) => {
    const [waiverRequired, setWaiverRequired] = useState(currentWaiverRequired ?? false);
    const [waiverTitle, setWaiverTitle] = useState<string | null>(currentWaiverTitle ?? null);
    const [waiverUrl, setWaiverUrl] = useState<string | null>(currentWaiverUrl ?? null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (visible) {
            setWaiverRequired(currentWaiverRequired ?? false);
            setWaiverTitle(currentWaiverTitle ?? null);
            setWaiverUrl(currentWaiverUrl ?? null);
            setUploadError(null);
        }
    }, [visible, currentWaiverRequired, currentWaiverTitle, currentWaiverUrl]);

    const handleFile = async (file: File) => {
        setUploadError(null);
        if (file.size > MAX_MB * 1024 * 1024) {
            setUploadError(`File must be under ${MAX_MB} MB.`);
            return;
        }
        setUploading(true);
        try {
            const ext = file.name.split('.').pop() || 'pdf';
            const path = `waivers/${sessionId || uuidv4()}_${Date.now()}.${ext}`;
            const url = await DataOps.uploadFile(file, path);
            if (url) {
                setWaiverUrl(url);
                setWaiverTitle(file.name);
            } else {
                setUploadError('Upload failed. Please try again.');
            }
        } catch {
            setUploadError('Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
        e.target.value = '';
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    const removeDocument = () => {
        setWaiverTitle(null);
        setWaiverUrl(null);
        setUploadError(null);
    };

    const handleSave = () => {
        onSave(
            waiverRequired,
            waiverRequired ? waiverTitle : null,
            waiverRequired ? waiverUrl : null,
        );
        onClose();
    };

    const handleToggle = (val: boolean) => {
        setWaiverRequired(val);
        if (!val) {
            setWaiverTitle(null);
            setWaiverUrl(null);
            setUploadError(null);
        }
    };

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <View style={styles.overlay}>
                <View style={styles.card}>
                    {/* Header */}
                    <View style={styles.header}>
                        <View style={styles.headerLeft}>
                            <MaterialCommunityIcons name="file-sign" size={20} color="#1D4ED8" />
                            <Text style={styles.headerTitle}>Waiver Settings</Text>
                        </View>
                        <Pressable onPress={onClose} style={styles.closeBtn}>
                            <MaterialCommunityIcons name="close" size={20} color="#666" />
                        </Pressable>
                    </View>

                    {/* Sub-heading */}
                    <Text style={styles.sessionLabel} numberOfLines={1}>{sessionTitle}</Text>

                    {/* Body */}
                    <View style={styles.body}>
                        {/* Toggle */}
                        <View style={styles.toggleRow}>
                            <View style={styles.toggleLeft}>
                                <Text style={styles.toggleTitle}>Require waiver for registration</Text>
                                <Text style={styles.toggleSub}>
                                    Parents must acknowledge the waiver before they can register their child.
                                </Text>
                            </View>
                            <Switch
                                value={waiverRequired}
                                onValueChange={handleToggle}
                                trackColor={{ false: '#E2E8F0', true: '#BFDBFE' }}
                                thumbColor={waiverRequired ? '#1D4ED8' : '#CBD5E1'}
                            />
                        </View>

                        {/* Upload area — only when waiver is required */}
                        {waiverRequired && (
                            <View style={styles.uploadSection}>
                                <Text style={styles.uploadLabel}>Waiver document</Text>
                                <Text style={styles.uploadHint}>
                                    Upload the PDF or Word document parents will review before registering. Max {MAX_MB} MB.
                                </Text>

                                {waiverUrl && waiverTitle ? (
                                    /* Existing document */
                                    <View style={styles.docCard}>
                                        <View style={styles.docIconWrap}>
                                            <MaterialCommunityIcons name="file-pdf-box" size={28} color="#DC2626" />
                                        </View>
                                        <View style={styles.docInfo}>
                                            <Text style={styles.docName} numberOfLines={2}>{waiverTitle}</Text>
                                            <Text style={styles.docStatus}>Uploaded</Text>
                                        </View>
                                        <View style={styles.docActions}>
                                            <Pressable
                                                style={styles.docActionBtn}
                                                onPress={() => window.open(waiverUrl, '_blank')}
                                            >
                                                <MaterialCommunityIcons name="open-in-new" size={16} color="#1D4ED8" />
                                            </Pressable>
                                            <Pressable style={styles.docActionBtn} onPress={removeDocument}>
                                                <MaterialCommunityIcons name="trash-can-outline" size={16} color="#DC2626" />
                                            </Pressable>
                                        </View>
                                    </View>
                                ) : (
                                    /* Drop zone */
                                    <div
                                        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                                        onDragLeave={() => setIsDragOver(false)}
                                        onDrop={handleDrop}
                                        style={{
                                            border: `2px dashed ${isDragOver ? '#1D4ED8' : '#CBD5E1'}`,
                                            borderRadius: 12,
                                            backgroundColor: isDragOver ? '#EFF6FF' : '#F8FAFC',
                                            padding: 24,
                                            display: 'flex',
                                            flexDirection: 'column' as const,
                                            alignItems: 'center',
                                            gap: 8,
                                            cursor: 'pointer',
                                            transition: 'all 0.15s',
                                        }}
                                        onClick={() => !uploading && fileInputRef.current?.click()}
                                    >
                                        {uploading ? (
                                            <>
                                                <ActivityIndicator size="small" color="#1D4ED8" />
                                                <Text style={styles.dropText}>Uploading…</Text>
                                            </>
                                        ) : (
                                            <>
                                                <MaterialCommunityIcons
                                                    name="cloud-upload-outline"
                                                    size={32}
                                                    color={isDragOver ? '#1D4ED8' : '#94A3B8'}
                                                />
                                                <Text style={styles.dropText}>
                                                    Drag and drop or{' '}
                                                    <Text style={styles.dropTextLink}>browse</Text>
                                                </Text>
                                                <Text style={styles.dropSubText}>PDF, DOC, DOCX</Text>
                                            </>
                                        )}
                                    </div>
                                )}

                                {uploadError && (
                                    <View style={styles.errorRow}>
                                        <MaterialCommunityIcons name="alert-circle-outline" size={14} color="#DC2626" />
                                        <Text style={styles.errorText}>{uploadError}</Text>
                                    </View>
                                )}

                                {/* Warning when required but no doc uploaded */}
                                {!waiverUrl && !uploading && !uploadError && (
                                    <View style={styles.warningRow}>
                                        <MaterialCommunityIcons name="information-outline" size={14} color="#D97706" />
                                        <Text style={styles.warningText}>
                                            Upload a document so parents know what they are signing.
                                        </Text>
                                    </View>
                                )}
                            </View>
                        )}
                    </View>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <Pressable style={styles.cancelBtn} onPress={onClose}>
                            <Text style={styles.cancelBtnText}>Cancel</Text>
                        </Pressable>
                        <Pressable
                            style={[styles.saveBtn, uploading && styles.saveBtnDisabled]}
                            onPress={handleSave}
                            disabled={uploading}
                        >
                            <Text style={styles.saveBtnText}>Save</Text>
                        </Pressable>
                    </View>
                </View>
            </View>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                style={{ display: 'none' }}
                onChange={handleInputChange}
            />
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    card: {
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        width: '100%',
        maxWidth: 520,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.18,
        shadowRadius: 24,
        elevation: 10,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingTop: 20,
        paddingBottom: 4,
    },
    headerLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    headerTitle: {
        fontSize: 17,
        fontWeight: '700',
        color: '#0F172A',
    },
    closeBtn: {
        padding: 4,
    },
    sessionLabel: {
        fontSize: 12,
        color: '#94A3B8',
        fontWeight: '500',
        paddingHorizontal: 20,
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
    },
    body: {
        padding: 20,
        gap: 20,
    },
    toggleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        backgroundColor: '#F8FAFC',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 12,
        padding: 16,
    },
    toggleLeft: {
        flex: 1,
    },
    toggleTitle: {
        fontSize: 14,
        fontWeight: '600',
        color: '#1E293B',
        marginBottom: 3,
    },
    toggleSub: {
        fontSize: 12,
        color: '#64748B',
        lineHeight: 18,
    },
    uploadSection: {
        gap: 10,
    },
    uploadLabel: {
        fontSize: 13,
        fontWeight: '600',
        color: '#374151',
    },
    uploadHint: {
        fontSize: 12,
        color: '#64748B',
        lineHeight: 18,
    },
    dropText: {
        fontSize: 13,
        color: '#475569',
        fontWeight: '500',
    },
    dropTextLink: {
        color: '#1D4ED8',
        fontWeight: '600',
    },
    dropSubText: {
        fontSize: 11,
        color: '#94A3B8',
    },
    docCard: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        backgroundColor: '#F0FDF4',
        borderWidth: 1,
        borderColor: '#BBF7D0',
        borderRadius: 12,
        padding: 14,
    },
    docIconWrap: {
        width: 44,
        height: 44,
        borderRadius: 10,
        backgroundColor: '#FEF2F2',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    docInfo: {
        flex: 1,
    },
    docName: {
        fontSize: 13,
        fontWeight: '600',
        color: '#166534',
        lineHeight: 18,
    },
    docStatus: {
        fontSize: 11,
        color: '#16A34A',
        marginTop: 2,
    },
    docActions: {
        flexDirection: 'row',
        gap: 4,
    },
    docActionBtn: {
        padding: 6,
        borderRadius: 8,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    errorRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: '#FEF2F2',
        borderRadius: 8,
        paddingHorizontal: 10,
        paddingVertical: 8,
    },
    errorText: {
        fontSize: 12,
        color: '#DC2626',
        flex: 1,
    },
    warningRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 6,
        backgroundColor: '#FFFBEB',
        borderRadius: 8,
        paddingHorizontal: 10,
        paddingVertical: 8,
    },
    warningText: {
        fontSize: 12,
        color: '#92400E',
        flex: 1,
        lineHeight: 17,
    },
    footer: {
        flexDirection: 'row',
        gap: 10,
        padding: 20,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
    },
    cancelBtn: {
        flex: 1,
        paddingVertical: 11,
        borderRadius: 10,
        alignItems: 'center',
        backgroundColor: '#F8FAFC',
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    cancelBtnText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#64748B',
    },
    saveBtn: {
        flex: 1,
        paddingVertical: 11,
        borderRadius: 10,
        alignItems: 'center',
        backgroundColor: '#1D4ED8',
    },
    saveBtnDisabled: {
        opacity: 0.5,
    },
    saveBtnText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#FFFFFF',
    },
});

export default WaiverEditModal;
